import axios, { AxiosError, InternalAxiosRequestConfig, isAxiosError } from 'axios';

import { ApiError } from './api.types';

import { getAccessToken } from '@/utils/authTokens';
import { logger } from '@/utils/logger/loggerInstance';

const apiUrl = import.meta.env.VITE_API_URL as string;

export const apiClient = axios.create({
  baseURL: apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const setupApiClientInterceptors = (handlers: {
  refreshAuthSession: () => Promise<void>;
  logOut: () => Promise<void>;
}) => {
  apiClient.interceptors.request.use((config) => {
    const accessToken = getAccessToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response, // Directly return successful responses.
    async (error: AxiosError) => {
      const originalRequest: CustomAxiosRequestConfig | undefined = error.config;

      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.

        logger.debug(`received HTTP 401 response, refreshing the token`);

        try {
          await handlers.refreshAuthSession();
          const accessToken = getAccessToken();
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          logger.debug(`access token refreshed and stored`);

          return apiClient(originalRequest); // Retry the original request with the new access token.
        } catch (refreshError) {
          logger.error('token refresh failed, logging out', { error: refreshError });

          await handlers.logOut();

          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error); // For all other errors, return the error as is.
    },
  );
};

export const isApiError = <T = unknown>(error: any): error is ApiError<T> => isAxiosError<T>(error);
